/**
 * Endpoint #51 — the global feed: every watched profile's posts on one screen (FEED_SPEC).
 *
 * Deliberately #26 with a different scope: the same `sort`/`dir` pair, the same opaque keyset
 * cursor and the same `FeedCardDTO`. What changes is *which* authors the page covers — `groupIds`
 * and `profileIds` replace #26's subject path and its group-only `authorIds` narrowing, and the
 * server resolves them (union, archived excluded) at query time, so a persisted selection follows
 * later membership changes.
 */
import { z } from 'zod';
import { POSTS_SORT_KEYS } from '../columns.js';
import { SORT_DIRS } from '../enums.js';
import { IdListParamSchema, MAX_FEED_LIMIT, ViewFilterParamsSchema } from './common.js';
/**
 * `#51 GET /api/feed`
 *
 * The §5.2 filter params **minus `authorIds`** — the scope params replace it, and an `authorIds=`
 * that survives from a copied view URL is simply stripped (Zod objects drop unknown keys), never a
 * 400. `q` stays in the schema and is honoured, but the client never sends it: search is Posts-only
 * (PRD), the #26 stance.
 */
export const GlobalFeedQuerySchema = ViewFilterParamsSchema.omit({ authorIds: true }).extend({
    /** Checked groups; resolved to their **active** members at query time. */
    groupIds: IdListParamSchema.optional(),
    /** Individually checked profiles; unioned with the groups' members. */
    profileIds: IdListParamSchema.optional(),
    sort: z.enum(POSTS_SORT_KEYS).default('publishedAt'),
    dir: z.enum(SORT_DIRS).default('desc'),
    /** Opaque cursor returned as `nextCursor`; absent on the first page. */
    cursor: z.string().max(400).optional(),
    limit: z.coerce.number().int().positive().max(MAX_FEED_LIMIT).default(MAX_FEED_LIMIT),
});
//# sourceMappingURL=globalFeed.js.map